// ============================================================
// LinkCasePickerModal.tsx
// Firm-wide case picker for the case-linking feature (and the appointment
// form's "link to a case"). Rebuilt on the shared ModalFrame (size lg) to
// the case-entry-modals mockup's "Open existing case" finder
// (casevo-case-entry-modals.html: .hsearch :48-50, .crow rows :107-123):
//   • search in the hero band's headerSlot (↑/↓ to move · Enter to link);
//   • case-list-style rows — name + ref, client · accident · defendant,
//     owner avatar / gold "You", stage pill;
//   • the shared caseMatchesQuery (utils/caseSearch.ts — the case list's own
//     search: name, ref, client, NI, id, accident/DOB dates);
//   • select a row → "Link case" (or double-click / Enter) → onPick(id).
//   • mode="select" swaps the link-flavoured copy for select wording (New bill).
// Behaviour kept from the previous picker: the firm-wide fetchCases({})
// list (the backend only returns cases the viewer may access, and the link
// endpoint re-checks target access — caseRoutes.ts:92 linkTargetAccess), the
// current case is never offered, already-linked cases can't be picked (now
// shown greyed as "Linked" instead of hidden), `busy` blocks picking, and
// `error` / load errors surface inline. The caller still owns linkCaseApi.
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from 'react'
import ModalFrame from './ModalFrame'
import { fetchCases } from '../../api/casevoApi'
import type { RawCase } from '../../types/case.types'
import type { CaseSummary } from '../layout/CaseListScreen'
import { caseName, caseRef } from '../../utils/caseName'
import { caseMatchesQuery } from '../../utils/caseSearch'
import { BTN_PRIMARY } from '../../styles/buttons'
import { useCasevoStore } from '../../store/useCasevoStore'
import { LockIcon, LOCKED_CASE_TIP } from './CaseLink'
import UserAvatar from './UserAvatar'

interface LinkCasePickerModalProps {
  isOpen: boolean
  onClose: () => void
  // The case being linked FROM — excluded from results (no self-link).
  currentCaseId: string
  // Case ids already linked to the current case — shown as "Linked" and not
  // pickable, so the user can't try to create a duplicate link.
  excludeCaseIds: string[]
  // Fired when the user picks a case to link. The caller runs linkCaseApi
  // + refresh; `busy` disables picking while that's in flight.
  onPick: (targetCaseId: string) => void
  busy?: boolean
  // Optional inline error (e.g. a failed link) surfaced at the top.
  error?: string | null
  // Copy overrides for non-link uses (the appointment form's case picker).
  title?: string
  actionLabel?: string
  // 'link' (default) keeps the case-linking wording ("Enter to link", "…can be
  // linked"); 'select' is for pickers that just CHOOSE a case (New bill), with
  // `helperText` as the line under the search box.
  mode?: 'link' | 'select'
  helperText?: string
}

// ── icons (Feather, design-language §6) ──
const P = {
  search: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  cal: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></>,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  check: <polyline points="20 6 9 17 4 12" />,
}
const Ic: React.FC<{ d: React.ReactNode; className?: string }> = ({ d, className = 'w-[13px] h-[13px]' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}>{d}</svg>
)

// Mockup .btn (:42) secondary — the Cancel button used by NewCaseModal.
const BTN_CANCEL =
  'inline-flex items-center text-sm font-medium rounded-lg px-5 py-2.5 border border-casevo-border text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus disabled:opacity-50'
// Mockup .grpH (:123).
const GRP_H = 'text-[10.5px] tracking-[0.16em] uppercase text-casevo-text-soft font-semibold px-[16px] pt-[12px] pb-[6px]'

const clientName = (c: RawCase): string => {
  const forename = c.clientForename || c.client?.forename || ''
  const surname = c.clientSurname || c.client?.surname || ''
  return `${forename} ${surname}`.trim() || 'Unnamed client'
}
// "12 Sep 2026" — the case list / mockup row date (.cmeta "Accident 12 Sep 2026").
// Date-only strings are UTC midnight, so format in UTC (no day shift).
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtDay = (iso: string): string => {
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return iso
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}
const stageOf = (c: RawCase): 'Draft' | 'Open' | 'Closed' =>
  c.closeDate ? 'Closed' : c.setupFinalised ? 'Open' : 'Draft'
// Lists may carry a `restricted` flag (Batch 4 locked rows); getCases doesn't
// today, but a row that does is shown locked + non-selectable.
const isRestricted = (c: RawCase): boolean => (c as RawCase & { restricted?: boolean }).restricted === true

const LinkCasePickerModal: React.FC<LinkCasePickerModalProps> = ({
  isOpen, onClose, currentCaseId, excludeCaseIds, onPick, busy = false, error = null,
  title = 'Link a case', actionLabel = 'Link case', mode = 'link', helperText,
}) => {
  const selecting = mode === 'select'
  const authUserId = useCasevoStore((s) => s.authUser?.id ?? null)
  const [cases, setCases] = useState<RawCase[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const rowRefs = useRef<Record<string, HTMLLIElement | null>>({})

  // Load the firm's full case list once per open (firm-wide — no filter).
  useEffect(() => {
    if (!isOpen) return
    setSearch('')
    setSelectedId(null)
    setLoadError(null)
    setLoading(true)
    let cancelled = false
    void fetchCases({})
      .then((data) => { if (!cancelled) setCases(data) })
      .catch(() => { if (!cancelled) setLoadError('Failed to load cases. Please try again.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [isOpen])

  // Exclusions + search. The current case is never offered; already-linked
  // cases are split out (shown, not pickable). Search = the case list's own
  // matcher (caseSearch.ts) — a superset of the old name/client/ref match.
  const linkedSet = useMemo(() => new Set(excludeCaseIds), [excludeCaseIds])
  const { pickable, linked } = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matches = cases.filter((c) => c.id !== currentCaseId && caseMatchesQuery(c as unknown as CaseSummary, q))
    return {
      pickable: matches.filter((c) => !linkedSet.has(c.id)),
      linked: matches.filter((c) => linkedSet.has(c.id)),
    }
  }, [cases, search, currentCaseId, linkedSet])
  const selectable = useMemo(() => pickable.filter((c) => !isRestricted(c)), [pickable])
  const totalOffered = useMemo(() => cases.filter((c) => c.id !== currentCaseId).length, [cases, currentCaseId])

  // Keep the selection valid as the search narrows.
  useEffect(() => {
    if (selectedId && !selectable.some((c) => c.id === selectedId)) setSelectedId(null)
  }, [selectable, selectedId])

  const pick = (id: string | null): void => {
    if (!id || busy) return
    onPick(id)
  }

  // ↑/↓ moves the selection (from the search box or the list), Enter links.
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return
    if (e.key === 'Enter') {
      if (selectedId) { e.preventDefault(); pick(selectedId) }
      return
    }
    if (selectable.length === 0) return
    e.preventDefault()
    const i = selectable.findIndex((c) => c.id === selectedId)
    const next = e.key === 'ArrowDown'
      ? selectable[Math.min(selectable.length - 1, i + 1)]
      : selectable[Math.max(0, i === -1 ? 0 : i - 1)]
    setSelectedId(next.id)
    rowRefs.current[next.id]?.scrollIntoView({ block: 'nearest' })
  }

  const row = (c: RawCase, kind: 'pick' | 'linked'): React.ReactNode => {
    const restricted = isRestricted(c)
    const disabled = kind === 'linked' || restricted || busy
    const sel = selectedId === c.id
    const stage = stageOf(c)
    const mine = !!authUserId && c.assignedToUserId === authUserId
    const owner = c.assignedTo
    return (
      <li key={c.id} ref={(el) => { rowRefs.current[c.id] = el }} role="option" aria-selected={sel} aria-disabled={disabled || undefined}
        className={`border-t border-casevo-border-soft first:border-t-0 transition-colors ${
          sel ? 'bg-[color-mix(in_srgb,var(--casevo-mint)_55%,transparent)]' : disabled ? '' : 'hover:bg-casevo-input'
        }`}>
        <button
          type="button"
          disabled={disabled}
          title={restricted ? LOCKED_CASE_TIP : kind === 'linked' ? 'Already linked to this case' : undefined}
          onClick={() => setSelectedId(c.id)}
          onDoubleClick={() => pick(c.id)}
          className={`w-full grid grid-cols-[4px_minmax(0,1fr)_auto] gap-[14px] items-center pr-[16px] py-[13px] text-left focus:outline-none casevo-focus ${
            disabled ? 'cursor-not-allowed' : ''
          }`}
        >
          <span className={`self-stretch rounded-r-[3px] ${sel ? 'bg-casevo-brand' : ''}`} />
          <span className={`min-w-0 ${kind === 'linked' ? 'opacity-60' : ''}`}>
            {restricted ? (
              <>
                <span className="flex items-center gap-[6px] text-[13px] text-casevo-text-soft"><LockIcon className="w-[14px] h-[14px]" />Restricted case</span>
                <span className="block text-[12.5px] text-casevo-text-soft mt-[4px]">You don’t have access to this case’s details</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-[8px] text-[15px] font-bold text-casevo-forest min-w-0">
                  <span className="truncate">{caseName(c)}</span>
                  <span className="font-mono text-[11.5px] font-medium text-casevo-text-soft shrink-0">{caseRef(c)}</span>
                </span>
                <span data-meta className="flex gap-[14px] text-[12.5px] text-casevo-text-soft mt-[4px] flex-wrap">
                  <span className="inline-flex items-center gap-[5px]"><Ic d={P.user} />{clientName(c)}</span>
                  {c.accidentDate && <span className="inline-flex items-center gap-[5px]"><Ic d={P.cal} />Accident {fmtDay(c.accidentDate)}</span>}
                  {c.defendantName && <span className="inline-flex items-center gap-[5px]"><Ic d={P.shield} />{c.defendantName}</span>}
                </span>
              </>
            )}
          </span>
          <span className="flex items-center gap-[10px]">
            {kind === 'linked' && (
              <span className="inline-flex items-center gap-[4px] text-[11px] font-semibold text-casevo-brand dark:text-[#8fd6b0]"><Ic d={P.link} className="w-[12px] h-[12px]" />Linked</span>
            )}
            {sel && (
              <span className="inline-flex items-center gap-[4px] text-[12px] font-semibold text-casevo-brand dark:text-[#8fd6b0]"><Ic d={P.check} />Selected</span>
            )}
            {mine ? (
              <span className="text-[10.5px] font-bold px-[7px] py-[2px] rounded-[5px] bg-casevo-gold text-[#3a2e08]">You</span>
            ) : owner ? (
              <UserAvatar userId={owner.id} fallbackName={`${owner.firstName} ${owner.lastName}`} size="rail" />
            ) : null}
            <span className={`text-[10.5px] font-bold tracking-[0.06em] uppercase px-[8px] py-[3px] rounded-[5px] ${
              stage === 'Closed' ? 'bg-casevo-input text-casevo-text-soft' : 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]'
            }`}>{stage}</span>
          </span>
        </button>
      </li>
    )
  }

  // Mockup .hsearch (:48-50) — white-alpha field on the hero band.
  const headerSearch = (
    <div>
      <div className="flex items-center gap-[10px] bg-white/[0.12] border border-white/25 rounded-[10px] px-[14px] py-[10px] focus-within:border-white/60">
        <Ic d={P.search} className="w-[17px] h-[17px] opacity-[0.85]" />
        <input
          ref={searchRef}
          type="text"
          aria-label="Search cases by name, client, reference, defendant or date"
          aria-controls="link-case-list"
          placeholder="Search by case name, client, reference or defendant…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onKeyDown}
          className="flex-1 min-w-0 bg-transparent border-0 p-0 text-[14px] text-white/95 placeholder:text-white/70 placeholder-white focus:outline-none"
        />
        <span className="hidden sm:inline text-[11px] bg-white/15 px-[6px] py-[2px] rounded-[4px] opacity-[0.85] whitespace-nowrap">{selecting ? '↑ ↓ to move · Enter to select' : '↑ ↓ to move · Enter to link'}</span>
      </div>
      <p className="mt-[10px] text-[12.5px] text-white/75">{helperText ?? (selecting ? 'Select a case.' : 'Any case in your firm you can access can be linked — including a different client’s.')}</p>
    </div>
  )

  const shownCount = pickable.length + linked.length
  const errMsg = error || loadError

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      // Stacks above a host modal (e.g. the appointment form at z-[70]).
      zIndexClassName="z-[80]"
      eyebrow={title === 'Link a case' ? 'Link case' : 'Choose case'}
      eyebrowIcon={<Ic d={selecting ? P.search : P.link} className="w-3.5 h-3.5" />}
      title={title}
      subtitle="Search by client, case name, reference or defendant."
      headerSlot={headerSearch}
      initialFocusRef={searchRef}
      bodyPadding={false}
      footerLeft={
        <span className="text-[12px] text-casevo-text-soft">
          {loading ? 'Loading cases…' : `${shownCount} of ${totalOffered} cases`}
        </span>
      }
      footerRight={
        <>
          <button type="button" onClick={onClose} className={BTN_CANCEL}>Cancel</button>
          <button type="button" onClick={() => pick(selectedId)} disabled={!selectedId || busy} aria-busy={busy}
            className={`${BTN_PRIMARY} text-sm rounded-lg px-5 py-2.5`}>
            <Ic d={selecting ? P.check : P.link} className="w-[15px] h-[15px]" />
            {busy ? (selecting ? 'Loading…' : 'Linking…') : actionLabel}
          </button>
        </>
      }
    >
      <div onKeyDown={onKeyDown}>
        {errMsg && (
          <div role="alert" className="mx-6 sm:mx-8 mt-4 px-3 py-2 rounded-lg text-xs bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]">
            {errMsg}
          </div>
        )}
        {loading ? (
          <ul aria-hidden="true" className="py-[6px]">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="grid grid-cols-[4px_minmax(0,1fr)_auto] gap-[14px] items-center pr-[16px] py-[13px] border-t border-casevo-border-soft first:border-t-0 animate-pulse">
                <span />
                <span><span className="block w-56 h-[14px] rounded bg-casevo-mint/60 mb-[7px]" /><span className="block w-80 max-w-full h-[11px] rounded bg-casevo-mint/40" /></span>
                <span className="w-[52px] h-[18px] rounded-[5px] bg-casevo-mint/50" />
              </li>
            ))}
          </ul>
        ) : !loadError && shownCount === 0 ? (
          <div className="py-[38px] px-6 text-center">
            <div className="w-[44px] h-[44px] mx-auto mb-[10px] rounded-[12px] bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] grid place-items-center">
              <Ic d={search.trim() || selecting ? P.search : P.link} className="w-[20px] h-[20px]" />
            </div>
            <p className="text-[14px] font-bold text-casevo-forest">{search.trim() ? 'No matching cases' : selecting ? 'No cases to choose from' : 'No other cases to link'}</p>
            <p className="text-[12.5px] text-casevo-text-soft mt-[4px]">
              {search.trim() ? `Nothing matches “${search.trim()}”. Try a surname, reference or date.` : selecting ? 'There are no cases in your firm you can access.' : 'There are no other cases in your firm you can access.'}
            </p>
          </div>
        ) : (
          <div id="link-case-list" role="listbox" aria-label="Cases" className="max-h-[420px] overflow-y-auto pb-[6px]">
            {pickable.length > 0 && (
              <>
                <div className={GRP_H}>{search.trim() ? 'Matches' : 'All cases'}</div>
                <ul>{pickable.map((c) => row(c, 'pick'))}</ul>
              </>
            )}
            {linked.length > 0 && (
              <>
                <div className={GRP_H}>Already linked</div>
                <ul>{linked.map((c) => row(c, 'linked'))}</ul>
              </>
            )}
          </div>
        )}
      </div>
    </ModalFrame>
  )
}

export default LinkCasePickerModal
